import { useEffect, useState, useRef, useCallback } from "react";
import { useChat } from "../hooks/useChat";
import dayjs from "dayjs";
import { message } from "antd";
import { deleteSession } from "../api/chatService";
import { MoreVertical, Trash2, Plus, MessageCircle } from "lucide-react";
import FloatingModuleSelectionModal from "./FloatingModuleSelectionModal";
import { useAppSelector } from "../store/hooks";

// Simple reusable confirmation modal
const CustomModal = ({ isOpen, onClose, onConfirm, title, message: modalMessage, variant }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-5xl w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <p className="text-gray-600 mb-6">{modalMessage}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded ${
              variant === "danger"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-skin-button-base hover:bg-skin-button-base"
            }`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const FloatingChatSidebar = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState(null);

  const scrollContainerRef = useRef(null);
  const observer = useRef(null);
  const loadingTriggerRef = useRef(null);

  const {
    currentSessionId,
    loadSessionMessages,
    initializeSession,
    setCurrentSessionId,
    setCurrentModule,
    sessions,
    fetchSessions,
    sessionsLoading,
    loadMoreSessions,
    hasMoreSessions,
    isLoadingMore,
    initializeDirectSession,
  } = useChat();

  const requireModuleSelection = useAppSelector(
    (state) => state.moduleSelection.requireModuleSelection
  );

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (
        target.isIntersecting &&
        hasMoreSessions &&
        !isLoadingMore &&
        !sessionsLoading
      ) {
        loadMoreSessions();
      }
    },
    [hasMoreSessions, isLoadingMore, loadMoreSessions, sessionsLoading]
  );

  useEffect(() => {
    const element = loadingTriggerRef.current;
    const container = scrollContainerRef.current;
    if (!element || !container) return;

    const options = { root: container, rootMargin: "50px", threshold: 0.1 };

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(handleObserver, options);
    observer.current.observe(element);

    return () => observer.current && observer.current.disconnect();
  }, [handleObserver, sessions.length]);

  // Close dropdown menu on outside click
  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSessionClick = (session) => {
    if (session.session_id === currentSessionId) return;
    loadSessionMessages(session.session_id);
    setCurrentSessionId(session.session_id);
    setCurrentModule(session.module_name);
  };

  const handleCreateChat = (moduleId, moduleName) => {
    initializeSession(moduleId, moduleName);
    setIsModalVisible(false);
  };

  const handleMenuClick = (e, sessionId) => {
    e.stopPropagation();
    setActiveMenuId(activeMenuId === sessionId ? null : sessionId);
  };

  const handleDeleteSession = () => {
    const token = localStorage.getItem("Token");
    deleteSession(token, { id: deleteSessionId })
      .then((response) => {
        message.success(response.message);
        setIsDeleteModalOpen(false);
        fetchSessions();
      })
      .catch((error) => {
        console.error("Failed to delete session:", error);
        message.error("Failed to delete session");
      });
  };

  const handleStartSession = () => {
    initializeDirectSession();
  };

  return (
    <div className="w-64 min-w-64 max-w-64 bg-skin-fill-secondary h-full overflow-hidden border-r border-skin-base flex flex-col transition-colors duration-300">
      {/* Header */}
      <div className="p-3 border-b border-skin-base">
        {requireModuleSelection ? (
          <button
            onClick={() => setIsModalVisible(true)}
            className="inline-flex items-center justify-center gap-2 font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 bg-skin-button-base text-white hover:bg-skin-button-hover focus:ring-2 focus:ring-skin-base focus:ring-offset-2 w-full"
          >
            <Plus size={16} />
            Start New Chat
          </button>
        ) : (
          <button
            onClick={handleStartSession}
            className="inline-flex items-center justify-center gap-2 font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 bg-skin-button-base text-white hover:bg-skin-button-hover focus:ring-2 focus:ring-skin-base focus:ring-offset-2 w-full"
          >
            <MessageCircle size={16} />
            Start Session
          </button>
        )}
      </div>

      {/* Session Header */}
      <div className="px-3 py-2 text-xs font-medium text-skin-muted uppercase tracking-wide">
        Sessions ({sessions.length})
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-hidden">
        {sessionsLoading && sessions.length === 0 ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-skin-button-base"></div>
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className="h-full overflow-y-auto space-y-1 px-2 scrollbar-thin"
          >
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => handleSessionClick(session)}
                className={`py-2.5 px-2 rounded-lg cursor-pointer transition-all duration-200 relative group ${
                  currentSessionId === session.session_id
                    ? "bg-primary-200 text-primary-900 dark:bg-primary-800 dark:text-primary-100"
                    : "hover:bg-skin-fill-muted text-skin-base"
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="flex-1 overflow-hidden">
                    <div
                      className="text-sm font-medium truncate"
                      title={session.module_name}
                    >
                      {session.module_name}
                    </div>
                    <div className="text-xs text-skin-muted truncate">
                      {session.session_id.slice(0, 8)}...
                    </div>
                    <div className="text-xs text-skin-muted">
                      {dayjs(session.created_at).format("MM/DD HH:mm")}
                    </div>
                  </div>

                  {/* Menu Button */}
                  <div className="relative flex items-center">
                    <button
                      onClick={(e) => handleMenuClick(e, session.session_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:bg-skin-fill-muted rounded text-skin-muted hover:text-skin-base"
                      title="More options"
                    >
                      <MoreVertical size={14} />
                    </button>

                    {activeMenuId === session.session_id && (
                      <div className="absolute right-0 top-6 pt-2 rounded-lg shadow-lg z-20 min-w-28">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteSessionId(session.session_id);
                            setIsDeleteModalOpen(true);
                          }}
                          className="w-full flex items-center justify-start gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-white bg-error hover:bg-error-light dark:hover:bg-error-DEFAULT/10 transition-colors duration-150"
                        >
                          <Trash2 size={12} />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Infinite Scroll Trigger */}
            {sessions.length > 0 && hasMoreSessions && (
              <div
                ref={loadingTriggerRef}
                className="py-3 flex justify-center"
                style={{ minHeight: "40px" }}
              >
                {isLoadingMore ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-skin-button-base"></div>
                    <span className="text-xs text-skin-muted">Loading...</span>
                  </div>
                ) : (
                  <div className="text-xs text-skin-muted opacity-60">
                    Scroll for more
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {sessions.length === 0 && !sessionsLoading && (
              <div className="text-center py-6 text-skin-muted px-2">
                <div className="text-2xl mb-2">💬</div>
                <div className="text-sm">No sessions yet</div>
                <div className="text-xs mt-1 opacity-75">
                  Start a new chat to begin
                </div>
              </div>
            )}

            {/* End of List */}
            {!hasMoreSessions && sessions.length > 0 && (
              <div className="text-center py-3 text-xs text-skin-muted opacity-60 border-t border-skin-base mt-2">
                <div>End of sessions</div>
                <div className="mt-1">{sessions.length} total</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <FloatingModuleSelectionModal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onStartChat={handleCreateChat}
      />

      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteSession}
        title="Delete Session"
        message="Are you sure you want to delete this session? This action cannot be undone."
        variant="danger"
      />
    </div>
  );
};

export default FloatingChatSidebar;
